
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { SEOHead } from '@/components/SEOHead';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { TabContent } from '@/components/dashboard/TabContent';
import { Navigate, useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { Camera, ArrowRight } from 'lucide-react';
import {
  buildDashboardReturnNext,
  resolveDashboardTab,
} from '@/utils/dashboardNavigation';

interface DashboardLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string | null;
  created_at?: string;
  bortle_class?: number | null;
  display_name?: string;
}

const Dashboard = () => {
  const { user, loading, authInitialized, isSubscribed, subscriptionData } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [selectedLocation, setSelectedLocation] = useState<DashboardLocation | null>(null);
  const [isInDetailView, setIsInDetailView] = useState(false);
  const [isWorkshopLeader, setIsWorkshopLeader] = useState(false);

  // Check if user is a workshop leader
  useEffect(() => {
    if (user) {
      supabase
        .from('workshop_leaders')
        .select('is_active, paid_through')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.is_active && data?.paid_through) {
            setIsWorkshopLeader(new Date(data.paid_through) > new Date());
          }
        });
    }
  }, [user]);
  
  // Get active tab from URL param or default to 'milkyway'
  const activeTab = resolveDashboardTab(searchParams.get('tab'));

  const handleLocationSelect = (location: DashboardLocation | null) => {
    setSelectedLocation(location);
    // Stay on locations tab when location is selected
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) {
        console.error('Error creating portal session:', error);
        toast({
          title: 'Error',
          description: 'Failed to open subscription management. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        console.error('No portal URL received');
        toast({
          title: 'Error',
          description: 'Failed to get subscription management URL.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to open subscription management.',
        variant: 'destructive',
      });
    }
  };

  if (!authInitialized || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50/30 flex items-center justify-center">
        <SEOHead
          title="Dashboard | Milky Way Planner"
          description="Your Milky Way Planner dashboard. Access your saved locations, Milky Way calendar, Moon Planner, and Trip Planner all in one place."
          noindex
        />
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h1 className="text-3xl font-light text-foreground tracking-tight">Planning Hub</h1>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const next = buildDashboardReturnNext(location.pathname, location.search);
    return <Navigate to={`/?next=${next}`} replace />;
  }

  return (
    <>
      <SEOHead
        title="Dashboard | Milky Way Planner"
        description="Your Milky Way Planner dashboard. Access your saved locations, Milky Way calendar, Moon Planner, and Trip Planner all in one place."
        noindex
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-yellow-50/30">
      <div className={`max-w-6xl mx-auto px-4 sm:px-6 py-8 relative`}>
        <DashboardHeader isSubscribed={isSubscribed} hideTopTabs={isInDetailView} isWorkshopLeader={isWorkshopLeader} />

        <TabContent
          activeTab={activeTab}
          selectedLocation={selectedLocation}
          subscriptionData={subscriptionData}
          onLocationSelect={handleLocationSelect}
          onManageSubscription={handleManageSubscription}
          onDetailViewChange={setIsInDetailView}
        />
      </div>

    </div>
    </>
  );
};

export default Dashboard;
